
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export const useMarketplaceFavorites = () => {
  const [favorites, setFavorites] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { toast } = useToast();

  // Récupérer la liste des favoris au chargement
  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setIsLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from('favorites')
          .select('service_id, is_favorite')
          .eq('user_id', user.id)
          .eq('is_favorite', true);

        if (error) throw error;

        const favoriteIds = data?.map(item => item.service_id) || [];
        setFavorites(favoriteIds);
      } catch (error) {
        console.error("Error fetching favorites:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFavorites();
  }, []);

  // Vérifier si un service est dans les favoris
  const isFavorite = (serviceId: string): boolean => {
    return favorites.includes(serviceId);
  };

  // Ajouter ou retirer un service des favoris
  const toggleFavorite = async (serviceId: string) => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Vous devez être connecté pour ajouter aux favoris"
        });
        return;
      }

      const isCurrentlyFavorite = favorites.includes(serviceId);
      
      // Check if a record already exists
      const { data: existingFavorite } = await supabase
        .from('favorites')
        .select('id')
        .eq('user_id', user.id)
        .eq('service_id', serviceId)
        .maybeSingle();
        
      if (existingFavorite) {
        // Update existing record
        await supabase
          .from('favorites')
          .update({ is_favorite: !isCurrentlyFavorite })
          .eq('id', existingFavorite.id);
      } else {
        // Insert new record
        await supabase
          .from('favorites')
          .insert({
            user_id: user.id,
            service_id: serviceId,
            is_favorite: true
          });
      }
      
      // Update local state
      if (isCurrentlyFavorite) {
        setFavorites(favorites.filter(id => id !== serviceId));
        toast({
          title: "Retiré des favoris",
          description: "Le produit a été retiré de vos favoris"
        });
      } else {
        setFavorites([...favorites, serviceId]);
        toast({
          title: "Ajouté aux favoris",
          description: "Le produit a été ajouté à vos favoris"
        });
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de modifier les favoris"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return { favorites, isFavorite, toggleFavorite, isLoading };
};
